/**
 * Shared full-screen error state for simulation modules.
 *
 * Mirrors the card-centered error layout the Reading, Listening, and
 * Writing interfaces use today.
 */

import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";

export interface SimulationModuleErrorProps {
  /** Primary error message. */
  readonly message: ReactNode;
  /** Optional secondary explanation (detail). */
  readonly detail?: ReactNode;
  /** Optional hint (e.g. "Please try again."). */
  readonly hint?: ReactNode;
  /** When true, the error card border is emphasized in the destructive color. */
  readonly destructiveBorder?: boolean;
}

export function SimulationModuleError({
  message,
  detail,
  hint,
  destructiveBorder = false,
}: SimulationModuleErrorProps) {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background">
      <Card
        className={
          destructiveBorder
            ? "p-8 text-center border-destructive max-w-md"
            : "p-8 text-center max-w-md"
        }
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="font-ui font-semibold text-destructive">{message}</p>
          {detail ? (
            <p className="font-ui text-sm text-muted-foreground">{detail}</p>
          ) : null}
          {hint ? (
            <p className="font-ui text-xs text-muted-foreground">{hint}</p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
